import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Trash, FileArrowDown, ShieldCheck, Warning } from "@phosphor-icons/react";

const clockCls = { overdue: "text-red-600 font-semibold", due_soon: "text-amber-700", current: "text-green-700", none: "text-muted-foreground" };

function Stat({ label, value, danger, testid }) {
  return (
    <div className="border border-border bg-card p-4">
      <div className="overline text-muted-foreground text-xs">{label}</div>
      <div data-testid={testid} className={`text-2xl font-bold mt-1 ${danger && value > 0 ? "text-red-600" : ""}`}>{value}</div>
    </div>
  );
}
const Row = ({ children }) => <div className="grid grid-cols-2 gap-3">{children}</div>;
const Check = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox data-testid={testid} checked={checked} onCheckedChange={(v) => onChange(!!v)} className="rounded-none" />{label}</label>
);

export default function Cip007Controls() {
  const { isRole } = useAuth();
  const canEdit = isRole("admin", "analyst");
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [assets, setAssets] = useState([]);
  const [modal, setModal] = useState(null);
  const [denied, setDenied] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [su, li] = await Promise.all([api.get("/cip007/controls/summary"), api.get("/cip007/controls")]);
      setSummary(su.data); setRecords(li.data.records); setAssets(li.data.assets); setDenied(false);
    } catch (e) { if (e.response?.status === 403) setDenied(true); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const doExport = async () => {
    try { await downloadBlob("/cip007/r345-export", "CIP007_R3_R4_R5_Controls.docx"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const documentedIds = new Set(records.map((r) => r.asset_id));
  const undocumented = assets.filter((a) => !documentedIds.has(a.id));
  const openEditor = async (assetId) => {
    try { const { data } = await api.get(`/cip007/controls/${assetId}`); setModal({ assetId, data }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (assetId) => { try { await api.delete(`/cip007/controls/${assetId}`); loadAll(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div data-testid="cip007-controls-page">
      <PageHeader title="CIP-007-6 R3 / R4 / R5 — Malware, Logging & Access Control"
        subtitle="R3 malicious code prevention · R4 security event monitoring · R5 system access control"
        action={<Button data-testid="c7c-export-btn" className="rounded-none bg-primary hover:bg-primary/90" onClick={doExport}><FileArrowDown size={15} className="mr-1" />R3/R4/R5 Evidence</Button>} />
      <div className="p-8 space-y-6">
        {denied && (
          <div data-testid="c7c-bcsi-denied" className="flex items-start gap-2 border border-amber-300 bg-amber-50 px-4 py-3 text-sm">
            <Warning size={18} className="mt-0.5 shrink-0 text-amber-600" />
            <p className="text-amber-800">BCSI access required. This module handles BES Cyber System Information — ask an administrator to grant you BCSI access to view and edit CIP-007 R3/R4/R5 controls.</p>
          </div>
        )}
        {summary && (
          <div data-testid="c7c-summary" className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Assets documented" value={`${summary.assets_documented} / ${summary.total_assets}`} testid="c7c-stat-documented" />
            <Stat label="Control gaps" value={summary.control_gaps} danger testid="c7c-stat-gaps" />
            <Stat label="Log reviews overdue (R4.4)" value={summary.log_reviews_overdue} danger testid="c7c-stat-reviews" />
            <Stat label="Stale signatures (R3.3)" value={summary.stale_signatures} danger testid="c7c-stat-signatures" />
          </div>
        )}
        <div className="border border-border bg-card">
          <div className="border-b border-border px-4 py-3 flex items-center justify-between">
            <div className="font-display font-bold text-sm">Documented control registers</div>
            {canEdit && undocumented.length > 0 && (
              <Select onValueChange={(v) => openEditor(v)}>
                <SelectTrigger data-testid="c7c-add-asset" className="rounded-none h-9 w-64 text-xs"><SelectValue placeholder="+ Document an asset…" /></SelectTrigger>
                <SelectContent>{undocumented.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border bg-secondary/40 text-left overline text-muted-foreground">
              <th className="px-4 py-2">Cyber Asset</th><th className="px-4 py-2">R3 Method</th><th className="px-4 py-2">R4 Logging</th><th className="px-4 py-2">R4 Review</th><th className="px-4 py-2">Gaps</th><th className="px-4 py-2"></th>
            </tr></thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.asset_id} data-testid={`c7c-row-${r.asset_id}`} className="border-b border-border">
                  <td className="px-4 py-2 font-medium">{r.asset_name}</td>
                  <td className="px-4 py-2">{r.r3_method || <span className="text-red-600">none</span>}</td>
                  <td className="px-4 py-2">{r.r4_logging_enabled ? <span className="text-green-700">On</span> : <span className="text-red-600 font-semibold">Off</span>}</td>
                  <td className="px-4 py-2"><span className={clockCls[r.r4_review_clock?.status || "none"]}>{r.r4_review_clock?.status === "none" ? "—" : r.r4_review_clock?.status}</span></td>
                  <td className="px-4 py-2">{r.gaps?.length > 0 ? <span className="text-red-600 font-semibold">{r.gaps.length}</span> : <span className="text-green-700">0</span>}</td>
                  <td className="px-4 py-2 flex gap-2">
                    {canEdit && <button data-testid={`c7c-edit-${r.asset_id}`} onClick={() => openEditor(r.asset_id)} className="text-xs underline text-muted-foreground hover:text-foreground">edit</button>}
                    {canEdit && <button data-testid={`c7c-del-${r.asset_id}`} onClick={() => del(r.asset_id)} className="text-muted-foreground hover:text-red-600"><Trash size={15} /></button>}
                  </td>
                </tr>
              ))}
              {records.length === 0 && <tr><td colSpan={6} className="px-4 py-4 text-muted-foreground text-sm">No control registers documented. Use "+ Document an asset" to start.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {modal && <ControlsEditor modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); loadAll(); }} />}
    </div>
  );
}

function ControlsEditor({ modal, onClose, onSaved }) {
  const [f, setF] = useState(modal.data || {});
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const num = (v) => (v === "" ? "" : parseInt(v, 10) || 0);

  const save = async () => {
    try {
      await api.put(`/cip007/controls/${modal.assetId}`, {
        ...f, asset_id: modal.assetId,
        r4_retention_days: parseInt(f.r4_retention_days, 10) || 0,
        r4_review_interval_days: parseInt(f.r4_review_interval_days, 10) || 15,
        r5_password_length: parseInt(f.r5_password_length, 10) || 0,
        r5_password_change_months: parseInt(f.r5_password_change_months, 10) || 0,
        r5_failed_attempt_limit: parseInt(f.r5_failed_attempt_limit, 10) || 0,
      });
      toast.success("Controls register saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} />R3/R4/R5 Controls — {f.asset_name || modal.assetId}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <section className="space-y-2">
            <div className="font-display font-bold text-sm border-b border-border pb-1">R3 — Malicious Code Prevention</div>
            <div><Label>R3.1 Method (deter / detect / prevent)</Label><Input data-testid="c7c-r3-method" value={f.r3_method || ""} onChange={(e) => set("r3_method", e.target.value)} placeholder="e.g. EDR / AV / application whitelisting" className="rounded-none mt-1" /></div>
            <Row>
              <Check label="Signature/pattern-based (R3.3)" checked={!!f.r3_signature_based} onChange={(v) => set("r3_signature_based", v)} testid="c7c-r3-sigbased" />
              <div><Label className="text-xs">Last signature update (R3.3)</Label><Input data-testid="c7c-r3-sigdate" type="date" value={f.r3_last_signature_update || ""} onChange={(e) => set("r3_last_signature_update", e.target.value)} className="rounded-none mt-1 h-8" /></div>
            </Row>
            <div><Label>R3.2 Mitigation process for detected malicious code</Label><Textarea data-testid="c7c-r3-mitigation" value={f.r3_mitigation_process || ""} onChange={(e) => set("r3_mitigation_process", e.target.value)} className="rounded-none mt-1" /></div>
          </section>

          <section className="space-y-2">
            <div className="font-display font-bold text-sm border-b border-border pb-1">R4 — Security Event Monitoring</div>
            <Row>
              <Check label="R4.1 Event logging enabled" checked={!!f.r4_logging_enabled} onChange={(v) => set("r4_logging_enabled", v)} testid="c7c-r4-logging" />
              <Check label="R4.2 Alerting enabled" checked={!!f.r4_alerting_enabled} onChange={(v) => set("r4_alerting_enabled", v)} testid="c7c-r4-alerting" />
            </Row>
            <Row>
              <div><Label className="text-xs">R4.3 Retention (days, ≥90)</Label><Input data-testid="c7c-r4-retention" type="number" min="0" value={f.r4_retention_days ?? 90} onChange={(e) => set("r4_retention_days", e.target.value)} className="rounded-none mt-1 h-8" /></div>
              <div><Label className="text-xs">R4.4 Review interval (days)</Label><Input data-testid="c7c-r4-interval" type="number" min="1" value={f.r4_review_interval_days ?? 15} onChange={(e) => set("r4_review_interval_days", e.target.value)} className="rounded-none mt-1 h-8" /></div>
            </Row>
            <div><Label className="text-xs">R4.4 Last log review date</Label><Input data-testid="c7c-r4-lastreview" type="date" value={f.r4_last_log_review || ""} onChange={(e) => set("r4_last_log_review", e.target.value)} className="rounded-none mt-1 h-8" /></div>
          </section>

          <section className="space-y-2">
            <div className="font-display font-bold text-sm border-b border-border pb-1">R5 — System Access Control</div>
            <Check label="R5.1 Interactive-user authentication enforced" checked={!!f.r5_auth_enforced} onChange={(v) => set("r5_auth_enforced", v)} testid="c7c-r5-auth" />
            <Check label="R5.2 Default/generic accounts inventoried" checked={!!f.r5_default_accounts_inventoried} onChange={(v) => set("r5_default_accounts_inventoried", v)} testid="c7c-r5-defaccts" />
            <Check label="R5.3 Shared-account authorized individuals identified" checked={!!f.r5_shared_account_users_identified} onChange={(v) => set("r5_shared_account_users_identified", v)} testid="c7c-r5-shared" />
            <Check label="R5.4 Known default passwords changed" checked={!!f.r5_default_passwords_changed} onChange={(v) => set("r5_default_passwords_changed", v)} testid="c7c-r5-defpw" />
            <Row>
              <div><Label className="text-xs">R5.5 Min password length</Label><Input data-testid="c7c-r5-pwlen" type="number" min="0" value={f.r5_password_length ?? 0} onChange={(e) => set("r5_password_length", e.target.value)} className="rounded-none mt-1 h-8" /></div>
              <div className="flex items-end pb-1"><Check label="R5.5 Complexity enforced" checked={!!f.r5_password_complexity} onChange={(v) => set("r5_password_complexity", v)} testid="c7c-r5-complexity" /></div>
            </Row>
            <Row>
              <div><Label className="text-xs">R5.6 Change every (months, 0=obsolescence)</Label><Input data-testid="c7c-r5-changemo" type="number" min="0" value={f.r5_password_change_months ?? 0} onChange={(e) => set("r5_password_change_months", e.target.value)} className="rounded-none mt-1 h-8" /></div>
              <div><Label className="text-xs">R5.7 Failed-attempt limit (0=alerts)</Label><Input data-testid="c7c-r5-faillimit" type="number" min="0" value={f.r5_failed_attempt_limit ?? 0} onChange={(e) => set("r5_failed_attempt_limit", e.target.value)} className="rounded-none mt-1 h-8" /></div>
            </Row>
            <Check label="R5.7 Alerts generated on failed-attempt threshold" checked={!!f.r5_alerts_on_failed} onChange={(v) => set("r5_alerts_on_failed", v)} testid="c7c-r5-alerts" />
          </section>

          <div className="border-t border-border pt-3"><Label>Reviewer</Label><Input data-testid="c7c-reviewer" value={f.reviewer || ""} onChange={(e) => set("reviewer", e.target.value)} className="rounded-none mt-1" /></div>
          <Button data-testid="c7c-save-btn" onClick={save} className="w-full rounded-none bg-primary hover:bg-primary/90">Save Controls Register</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
